/**
 * `/calls` for a business on the simplified view: a call register for a tablet on a counter.
 *
 * THE SAME CALLS AS THE LOG, DRAWN FOR A DIFFERENT READER. An auto repair shop reads this
 * with greasy hands between jobs; a boat broker reads the log at a desk. So this page has
 * the list on the left and one call on the right, both at a type size readable from a step
 * back, and it has none of what the log is for: no filters, no search, no test calls, no
 * quality meter, no money. Every one of those is still in the standard view, one switch
 * away on /settings, and the gear in the top bar goes there from here.
 *
 * ONE FILTER, AND IT IS THE ONE QUESTION ASKED AT A COUNTER: is anything waiting on me.
 * Three big tabs over the list - every call, the ones waiting on a person, the ones a
 * person has marked handled - on the same `?open=` key the log's dropdown writes. So the
 * overview's "Needs your attention" link lands here on "Waiting on you", with the tab that
 * narrowed the list lit up on screen. EVERY OTHER KEY IN THE ADDRESS BAR IS IGNORED: a
 * `?band=hot` carried over from the log would narrow the register with nothing here saying
 * so, and a list that is short for a reason nobody can see is the bug filters cause.
 *
 * "MARKED HANDLED" AND NOT "HANDLED", because the two are different populations. The pill
 * on a row says Handled for a call the assistant dealt with as well as for one a person
 * closed; the tab is `open=false`, which is only the second. The word "marked" is the
 * difference, said on the button rather than left for somebody to discover.
 *
 * THE CALL ON THE RIGHT IS THE ROUTE'S CHILD, READ FROM THE ADDRESS BAR. `/calls/412` is a
 * real URL here as it is in the log - a link out of an email lands on that call, and the
 * back button walks through the calls somebody opened - but where the log opens a dialog
 * over the list, this page draws the call in its own pane beside it, and the child route
 * itself renders nothing (see `CallRoute`). With no call named, the pane shows the newest,
 * as the prototype this came from did, so the screen is never half empty.
 *
 * UNDER 900px THE TWO HALVES STACK INTO ONE, and which one shows is whether a call is named
 * in the address bar: the list by default, the call once one is tapped, with a big way back
 * at its top. Two scrolling panes side by side on a phone is a trap, not a layout.
 */

import { Outlet, useMatch, useNavigate, useSearchParams } from "react-router-dom";
import { useCallPages } from "./Calls";
import { useAgent } from "../currentAgent";
import { SimpleRow } from "../components/SimpleRow";
import { SimpleCallPane } from "../components/SimpleCallPane";
import { callStatus } from "../components/Handled";
import { Empty, Skeleton } from "../components/States";
import { Check, Phone, User } from "../components/Icons";
import { Button, Segmented, cx } from "../ui";

type Showing = "all" | "waiting" | "handled";

/** The line under the heading, for the tab on screen. See where it is drawn. */
function tally(show: Showing, calls: number, waiting: number, more: boolean): string {
  const sofar = more ? " so far" : "";
  if (show === "handled") return calls ? `${calls} marked handled${sofar}` : "None marked handled yet";
  const open = show === "waiting" ? calls : waiting;
  return open ? `${open} waiting on you${sofar}` : "Nothing waiting on you";
}

/** `?open=` as the register's tab. Anything but the two values the Worker reads is "all". */
function showing(open: string | null): Showing {
  return open === "true" ? "waiting" : open === "false" ? "handled" : "all";
}

export function SimpleCalls() {
  const [params] = useSearchParams();
  const current = useAgent();
  const tz = current.timezone;

  // The one filter, and nothing else. See this file's header.
  const show = showing(params.get("open"));
  const kept = new URLSearchParams();
  if (show !== "all") kept.set("open", show === "waiting" ? "true" : "false");
  const query = kept.toString();
  const { rows, error, loading, loadingMore, nextCursor, loadMore, context } = useCallPages(query);
  const navigate = useNavigate();

  /*
   * A NEW FILTER DROPS THE OPEN CALL and lands on the list, whose pane then shows the first
   * row of the new one. Keeping `/calls/412` while switching to "Waiting on you" would leave
   * a handled call in the pane beside a list it is not in, with no row highlighted for it.
   */
  const filter = (next: Showing) => {
    navigate(next === "all" ? `/${current.slug}/calls` : `/${current.slug}/calls?open=${next === "waiting"}`);
  };

  const match = useMatch("/:agent/calls/:id");
  const picked = match?.params.id;
  const selectedId = picked ?? (rows[0] ? String(rows[0].id) : undefined);
  const waiting = rows.filter((call) => callStatus(call).kind === "open").length;

  return (
    <div
      className={cx(
        "grid h-[calc(100dvh-var(--spacing-bar))] grid-cols-[minmax(300px,38%)_minmax(0,1fr)] gap-4 p-4",
        "max-[900px]:grid-cols-[minmax(0,1fr)] max-[900px]:p-3",
      )}
    >
      <section
        aria-label="Call register"
        className={cx(
          "flex min-h-0 flex-col overflow-hidden rounded-xl border border-line bg-paper",
          picked && "max-[900px]:hidden",
        )}
      >
        <header className="flex-none px-5 pt-5 pb-3">
          <h1 className="text-5xl font-semibold tracking-[-0.03em] text-ink">Call register</h1>
          {/*
            ONE LINE, ALWAYS THERE, AT ONE HEIGHT. It used to appear on "All calls" and
            vanish on the other two tabs, so the tabs and the list under them jumped by a
            line every time one was pressed. The words change with the tab; the space they
            take does not, and while a list loads the line is held empty rather than removed.
            Counted over the calls loaded so far, and it says so: there is no total on the
            wire, the same honesty the log's "so far" keeps.
          */}
          <p className="nums mt-2 min-h-7 text-xl text-ink-soft">
            {!loading && !error && tally(show, rows.length, waiting, Boolean(nextCursor))}
          </p>
          <div className="mt-4">
            <Segmented
              size="large"
              label="Which calls"
              value={show}
              options={[
                { value: "all", label: "All calls" },
                { value: "waiting", label: "Waiting on you" },
                { value: "handled", label: "Marked handled" },
              ]}
              onChange={filter}
            />
          </div>
        </header>

        {/* `@container`, which is what the rows measure to decide whether their pill fits
            beside the words. See `SimpleRow`. */}
        <div className="@container min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          {error && (
            <div className="p-3">
              <Empty
                compact
                tone={error.status === 401 ? undefined : "alert"}
                icon={error.status === 401 ? User : undefined}
                title={error.status === 401 ? "Your session has expired" : "Couldn't load calls"}
                body={
                  error.status === 401 ? "You were signed out while this page was open." : error.message
                }
                action={{ label: "Try again", onClick: () => window.location.reload() }}
              />
            </div>
          )}

          {loading && !rows.length && (
            <div className="grid gap-2 px-2">
              {Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} height={88} />
              ))}
            </div>
          )}

          {!error && !loading && !rows.length && (
            <div className="p-3">
              {show === "all" ? (
                <Empty
                  compact
                  icon={Phone}
                  title="No calls yet"
                  body="Calls appear here within a second of the caller hanging up."
                />
              ) : (
                /* A filtered list that came back empty is good news under "Waiting on you",
                   and says so - with the way back to every call, rather than a dead end. */
                <Empty
                  compact
                  icon={show === "waiting" ? Check : Phone}
                  title={show === "waiting" ? "All caught up" : "Nothing here yet"}
                  body={
                    show === "waiting"
                      ? "Every call that needed a person has been dealt with."
                      : "Calls you mark as handled show up here."
                  }
                  action={{ label: "Show all calls", onClick: () => filter("all") }}
                />
              )}
            </div>
          )}

          {rows.length > 0 && (
            <ul className="grid list-none p-0">
              {rows.map((call, index) => (
                <li key={call.id} className={cx(index > 0 && "border-t-2 border-line-soft")}>
                  <SimpleRow
                    call={call}
                    href={context.hrefFor(call.id)}
                    tz={tz}
                    selected={String(call.id) === selectedId}
                  />
                </li>
              ))}
            </ul>
          )}

          {nextCursor && (
            <div className="px-2 pt-3">
              <Button
                size="large"
                className="w-full"
                onClick={() => void loadMore()}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading…" : "Show older calls"}
              </Button>
            </div>
          )}
        </div>
      </section>

      <section
        aria-label="The selected call"
        className={cx(
          "min-h-0 overflow-y-auto rounded-xl border border-line bg-paper",
          !picked && "max-[900px]:hidden",
        )}
      >
        {selectedId ? (
          <SimpleCallPane
            // Keyed so a different call starts from nothing rather than from the last one's
            // transcript dialog and saved state.
            key={selectedId}
            id={selectedId}
            row={rows.find((call) => String(call.id) === selectedId)}
            backHref={context.listHref}
            onChanged={context.replace}
          />
        ) : (
          !loading && (
            <div className="p-6">
              <Empty compact icon={Phone} title="Pick a call" body="Its details show up here." />
            </div>
          )
        )}
      </section>

      {/* The child route, kept so `/calls/:id` stays a real URL in this view. It renders
          nothing here; the pane above reads the id itself. */}
      <Outlet context={context} />
    </div>
  );
}
